import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminSubpage from "../components/AdminSubpage";
import { productsAPI, styleCollectionsAPI, uploadAPI } from "../services/api";

const TYPES = ["celebrity", "lifestyle", "trend", "editorial"];

const emptyForm = {
  title: "",
  title_ar: "",
  slug: "",
  description: "",
  description_ar: "",
  tagline: "",
  tagline_ar: "",
  cover_image_url: "",
  type: "lifestyle",
  is_active: true,
  is_featured: false,
  sort_order: 0,
  meta_title: "",
  meta_description: "",
  meta_title_ar: "",
  meta_description_ar: "",
};

const StyleCollectionFormPage = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(collectionId);
  const [formData, setFormData] = useState(emptyForm);
  const [curatedProducts, setCuratedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await styleCollectionsAPI.getById(collectionId);
        const c = response.data?.collection;
        const rows = response.data?.products || [];
        if (!cancelled && c) {
          setFormData({
            title: c.title || "",
            title_ar: c.title_ar || "",
            slug: c.slug || "",
            description: c.description || "",
            description_ar: c.description_ar || "",
            tagline: c.tagline || "",
            tagline_ar: c.tagline_ar || "",
            cover_image_url: c.cover_image_url || "",
            type: c.type || "lifestyle",
            is_active: Boolean(c.is_active),
            is_featured: Boolean(c.is_featured),
            sort_order: c.sort_order ?? 0,
            meta_title: c.meta_title || "",
            meta_description: c.meta_description || "",
            meta_title_ar: c.meta_title_ar || "",
            meta_description_ar: c.meta_description_ar || "",
          });
          setCuratedProducts(
            rows.map((row, index) => ({
              product_id: row.product_id,
              ranking: row.ranking ?? index,
              product: row.product,
            })),
          );
        }
      } catch (error) {
        if (!cancelled)
          toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collectionId, isEdit]);

  const searchProducts = async () => {
    const q = productSearch.trim();
    if (!q) return;
    try {
      const res = await productsAPI.getAll({ search: q, limit: 15, page: 1 });
      const list = res.data?.products || res.data?.data || [];
      setSearchResults(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const addProduct = (product) => {
    if (curatedProducts.some((p) => p.product_id === product.id)) {
      toast.info("Product already in collection");
      return;
    }
    setCuratedProducts((prev) => [
      ...prev,
      { product_id: product.id, ranking: prev.length, product },
    ]);
  };

  const removeProduct = (productId) => {
    setCuratedProducts((prev) =>
      prev
        .filter((p) => p.product_id !== productId)
        .map((p, i) => ({ ...p, ranking: i })),
    );
  };

  const moveProduct = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= curatedProducts.length) return;
    const copy = [...curatedProducts];
    const tmp = copy[index];
    copy[index] = copy[next];
    copy[next] = tmp;
    setCuratedProducts(copy.map((p, i) => ({ ...p, ranking: i })));
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const url = response.data?.image_url;
      if (!url) throw new Error("No image URL returned");
      setFormData((prev) => ({ ...prev, cover_image_url: url }));
      toast.success("Cover image uploaded");
    } catch (error) {
      toast.error(
        "Upload failed: " + (error.response?.data?.message || error.message),
      );
    } finally {
      setUploadingCover(false);
    }
  };

  const buildPayload = () => ({
    title: formData.title.trim(),
    title_ar: formData.title_ar?.trim() || null,
    slug: formData.slug.trim() || undefined,
    description: formData.description.trim() || null,
    description_ar: formData.description_ar?.trim() || null,
    tagline: formData.tagline.trim() || null,
    tagline_ar: formData.tagline_ar?.trim() || null,
    cover_image_url: formData.cover_image_url.trim() || null,
    type: formData.type,
    is_active: Boolean(formData.is_active),
    is_featured: Boolean(formData.is_featured),
    sort_order: Number(formData.sort_order) || 0,
    meta_title: formData.meta_title.trim() || null,
    meta_description: formData.meta_description.trim() || null,
    meta_title_ar: formData.meta_title_ar?.trim() || null,
    meta_description_ar: formData.meta_description_ar?.trim() || null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await styleCollectionsAPI.update(collectionId, buildPayload());
        toast.success("Collection updated");
      } else {
        const res = await styleCollectionsAPI.create(buildPayload());
        const id = res.data?.collection?.id;
        toast.success("Collection created");
        if (id) {
          navigate(`/style-collections/${id}/edit`);
          return;
        }
      }
      navigate("/style-collections");
    } catch (error) {
      let message = isEdit ? "Error updating: " : "Error creating: ";
      if (error.response?.data?.errors) {
        message += Object.values(error.response.data.errors).flat().join(" ");
      } else {
        message += error.response?.data?.message || error.message;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const saveProducts = async () => {
    if (!isEdit) {
      toast.error("Save the collection first");
      return;
    }
    setSavingProducts(true);
    try {
      await styleCollectionsAPI.syncProducts(
        collectionId,
        curatedProducts.map((p, i) => ({
          product_id: p.product_id,
          ranking: p.ranking ?? i,
        })),
      );
      toast.success("Products saved");
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSavingProducts(false);
    }
  };

  const title = isEdit ? "Edit style collection" : "New style collection";

  return (
    <AdminSubpage title={title} backTo="/style-collections">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title (English) *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Title (Arabic)</label>
              <input
                type="text"
                dir="rtl"
                value={formData.title_ar}
                onChange={(e) =>
                  setFormData({ ...formData, title_ar: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Slug (optional)</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                placeholder="dubai-quiet-luxury"
              />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Tagline (English)</label>
              <input
                type="text"
                value={formData.tagline}
                onChange={(e) =>
                  setFormData({ ...formData, tagline: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Description (English)</label>
              <textarea
                rows={5}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Description (Arabic)</label>
              <textarea
                rows={5}
                dir="rtl"
                value={formData.description_ar}
                onChange={(e) =>
                  setFormData({ ...formData, description_ar: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Cover image URL</label>
              <input
                type="text"
                value={formData.cover_image_url}
                onChange={(e) =>
                  setFormData({ ...formData, cover_image_url: e.target.value })
                }
              />
              <input
                type="file"
                accept="image/*"
                style={{ marginTop: 8 }}
                onChange={(e) => handleCoverUpload(e.target.files?.[0])}
                disabled={uploadingCover}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />{" "}
                Active
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) =>
                    setFormData({ ...formData, is_featured: e.target.checked })
                  }
                />{" "}
                Featured on homepage
              </label>
            </div>
            <div className="form-group">
              <label>Sort order</label>
              <input
                type="number"
                min={0}
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Meta title</label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) =>
                  setFormData({ ...formData, meta_title: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Meta description</label>
              <textarea
                rows={2}
                value={formData.meta_description}
                onChange={(e) =>
                  setFormData({ ...formData, meta_description: e.target.value })
                }
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? "Saving…"
                : isEdit
                  ? "Update collection"
                  : "Create collection"}
            </button>
          </form>

          {isEdit ? (
            <div
              style={{
                marginTop: 40,
                borderTop: "1px solid #eee",
                paddingTop: 24,
              }}
            >
              <h3>Curated products</h3>
              <p style={{ fontSize: 13, color: "#666" }}>
                Search marketplace products and set display order.
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <input
                  type="text"
                  placeholder="Search by title or brand"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={searchProducts}
                >
                  Search
                </button>
              </div>
              {searchResults.length > 0 ? (
                <ul style={{ listStyle: "none", padding: 0, marginTop: 12 }}>
                  {searchResults.map((p) => (
                    <li
                      key={p.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "8px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <span>
                        {p.title} {p.brand ? `· ${p.brand}` : ""}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => addProduct(p)}
                      >
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <ol style={{ marginTop: 20, paddingLeft: 20 }}>
                {curatedProducts.map((row, index) => (
                  <li key={row.product_id} style={{ marginBottom: 8 }}>
                    <span>{row.product?.title || row.product_id}</span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ marginLeft: 8 }}
                      onClick={() => moveProduct(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ marginLeft: 4 }}
                      onClick={() => moveProduct(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      style={{ marginLeft: 8 }}
                      onClick={() => removeProduct(row.product_id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                className="btn btn-primary"
                style={{ marginTop: 12 }}
                onClick={saveProducts}
                disabled={savingProducts}
              >
                {savingProducts ? "Saving products…" : "Save product list"}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </AdminSubpage>
  );
};

export default StyleCollectionFormPage;
